import { useEffect } from 'react';
import { useDesignPreviewStore } from '@/stores/design-preview-store';
import {
  DESIGN_PREVIEW_APPLY,
  DESIGN_PREVIEW_READY,
  DESIGN_PREVIEW_QUERY_FLAG,
} from './preview-messages';

/**
 * Storefront-side listener for the Design Sandbox live preview.
 *
 * Mounted once in App, but only activates when the page is loaded inside an
 * iframe with `?design_preview=1` (i.e. the sandbox host's preview frame). It
 * listens for the host's `rumio:design-preview` postMessage and writes the
 * candidate theme_config into the design-preview store, which `useSettings()`
 * substitutes for the live theme_config — so the whole storefront re-renders
 * with the candidate, for this frame's session only. Nothing is persisted and
 * no other visitor is affected.
 *
 * Renders nothing.
 */
export default function DesignPreviewBridge() {
  const setPreviewThemeConfig = useDesignPreviewStore((s) => s.setPreviewThemeConfig);
  const setIsPreviewFrame = useDesignPreviewStore((s) => s.setIsPreviewFrame);

  useEffect(() => {
    // Guard: only ever active inside an iframe explicitly flagged for preview.
    // A top-level window (a real visitor) can never enter preview mode.
    const inIframe = typeof window !== 'undefined' && window.parent && window.parent !== window;
    const flagged = new URLSearchParams(window.location.search).get(DESIGN_PREVIEW_QUERY_FLAG) === '1';
    if (!inIframe || !flagged) return;

    const origin = window.location.origin;
    setIsPreviewFrame(true);

    function onMessage(e: MessageEvent) {
      // Same-origin only — admin panel and storefront share an origin.
      if (e.origin !== origin) return;
      const data = e.data as { type?: string; themeConfig?: unknown; dark?: unknown } | null;
      if (!data || typeof data !== 'object') return;
      if (data.type === DESIGN_PREVIEW_APPLY) {
        setPreviewThemeConfig((data.themeConfig as any) ?? null);
        // Force the preview's light/dark mode when the host specifies it.
        // useTheme only re-applies `.dark` on its own toggle, so this holds.
        if (typeof data.dark === 'boolean') {
          document.documentElement.classList.toggle('dark', data.dark);
        }
      }
    }

    window.addEventListener('message', onMessage);
    // Announce readiness so the host pushes the current candidate (covers the
    // race where the host's first push fires before this listener mounts).
    window.parent.postMessage({ type: DESIGN_PREVIEW_READY }, origin);

    return () => {
      window.removeEventListener('message', onMessage);
      setPreviewThemeConfig(null);
      setIsPreviewFrame(false);
    };
  }, [setPreviewThemeConfig, setIsPreviewFrame]);

  return null;
}
